import React, {useCallback, useState} from 'react';
import {Form, Formik, FormikHelpers, FormikProps} from "formik";
import {Event, EventFormData} from "types/Event";
import {DateTime} from "luxon";
import Label from "components/Form/Label";
import Field from "components/Form/Field";
import Selector from "components/Form/Selector";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {EventType} from "types/EventType";
import Toggle from "components/Form/Toggle";
import Uploader from "components/Form/Uploader";
import {PhotographIcon} from "@heroicons/react/outline";
import {useOperator} from "context/OperatorContext";
import RepetitionBuilder from "components/Form/RepetitionBuilder";
import FormControls from "components/FormControls";
import {Frequency, RRule} from "rrule";
import {PlaceData} from "../../../types/Place";
import ShortcutModal from "../../../components/Shortcut/ShortcutModal";
import PlaceForm from "../../Place/Form/PlaceForm";
import {invalidate} from "../../../services/query-client";
import {ToggleSwitch} from "../../../components/Form/Form/Control";
import LinkEditor from "../../../components/Form/LinkEditor";
import {useAuth} from "../../../context/AuthContext";
import {Site} from "../../../types/Site";
import {useLinkOptions} from "../../../hooks/useLinkOptions";
import {DateField} from "../../../components/Form/Form/Field";
import {formattedDateTime, isSameTimezone, systemZone} from "../../../services/date";
import {fixRRuleUntil} from "../../../services/recurrence";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";
import * as Yup from "yup";
import FieldHelp from "../../../components/Form/Form/Field/FieldHelp";
import {urlTest} from "../../../services/validation-schemas";
import {TextAreaField} from "../../../components/Form/Form";
import {useIsCurrentOperatorAllowed} from "../../../hooks/useIsCurrentOperatorAllowed";
import {getFirstEntityWithItems} from "../../../components/EntitySelectorModal/EntitySelectorModal.helpers";
import {EntitySelector} from "../../../components/EntitySelector";
import {
  SelectedItemsData
} from "../../../components/EntitySelector/EntitySelector.types";
import {ENTITY_TYPES} from "../../../components/EntitySelector/EntitySelector.constants";
import {createSelectedLocationItem} from "./EventForm.helpers";

// Helpers ----

function eventTypeAsItem(eventType: EventType) {
  return {
    id: eventType.id || 0,
    name: eventType.name,
    icon: eventType.icon
  }
}

function siteAsItem(site: Site): SiteOption {
  return {
    id: site.id || 0,
    name: site.name,
  }
}

// Types ----

type SiteOption = { id: number; name: string }

type EventFormProps = {
  event?: Event | null
  onSubmit: (values: EventFormData, helpers: FormikHelpers<EventFormData>, siteId?: number) => void
}

// Component ----

function EventForm(props: EventFormProps) {
  const {termFor, isMultiSite, settings} = useOperator();
  const auth = useAuth();
  const linkOptions = useLinkOptions('event');
  const isBookableOperator = useIsCurrentOperatorAllowed(['lhp']);

  const [mediaUploading, setMediaUploading] = useState<boolean>(false);
  const [addingPlace, setAddingPlace] = useState<boolean>(false);

  const [timezone, setTimezone] = useState<string>(props.event?.timezone ?? DateTime.local().zoneName);
  const [repeats, setRepeats] = useState<boolean>(!!props.event?.is_recurring);
  const [offsite, setOffsite] = useState<boolean>(props.event?.location?.type === 'place' || false);

  const {mutateAsync: storePlace} = useMutation((data: PlaceData) => ngage.places.store(data))

  const {data: eventTypes, isLoading: typesLoading} = useQuery(
    ['event-types.all', {page: 1}],
    () => ngage.eventTypes.all(1),
    {enabled: auth.can('event-types:read')}
  )

  const {data: sites, isLoading: sitesLoading} = useQuery(
    ['sites.all', {page: 1}],
    () => ngage.sites.filtered({page: 1}),
    {enabled: auth.can('sites:read')}
  )

  const isUpdating = !!props.event;
  const isEventSeriesOriginal = !!props.event?.is_recurring && !props.event?.original_event_id;
  const isEventSeriesInstance = !!props.event?.original_event_id;

  async function addPlace(values: PlaceData) {
    await storePlace(values)
    await invalidate('places.all')
    setAddingPlace(false)
  }

  function selectSite(site: SiteOption, formik: FormikProps<EventFormData>) {
    // If the site has changed, reset the location fields
    if (site.id !== formik.values.site_id) {
      formik.setFieldValue('site_id', site.id).then(_ => {
        // Validate the form field
        formik.validateField('site_id').then(_ => {
          void formik.setFieldTouched('site_id', true);
        });
      }).catch(console.error);
      formik.setFieldValue('location_type', undefined).catch(console.error);
      formik.setFieldValue('location_id', undefined).catch(console.error);
    }
  }

  const selectLocation = useCallback((items: SelectedItemsData, formik: FormikProps<EventFormData>) => {
    const firstEntity = getFirstEntityWithItems(items);

    if (!firstEntity) {
      formik.setFieldValue('location_id', 0).then(_ => {
        // Validate the form field
        formik.validateField('location_id').then(_ => {
          void formik.setFieldTouched('location_id', true);
        });
      }).catch(console.error);
      formik.setFieldValue('location_type', '').catch(console.error);
      return;
    }

    const {type: locationType, item: location} = firstEntity;
    const newLocation = {...location, type: locationType};

    formik.setFieldValue('location_id', newLocation.id).then(_ => {
      // Validate the form field
      formik.validateField('location_id').then(_ => {
        void formik.setFieldTouched('location_id', true);
      });
    }).catch(console.error);
    formik.setFieldValue('location_type', locationType).catch(console.error);

    let timezone = "UTC";

    if ("site" in newLocation) {
      timezone = newLocation.site?.timezone ?? timezone;
    } else if ("zone" in newLocation) {
      timezone = newLocation.zone.site.timezone;
    } else if ("timezone" in newLocation) {
      timezone = newLocation.timezone ?? timezone;
    }

    setTimezone(timezone);
  }, [])

  function toggleOffsite(enabled: boolean, {setFieldValue, setFieldTouched, validateField}: FormikHelpers<EventFormData>) {
    // Reset site and location fields
    Promise.all([
      setFieldValue('site_id', undefined).catch(console.error),
      setFieldValue('location_id', 0).catch(console.error),
      setFieldValue('location_type', '').catch(console.error),
    ]).then(_ => {
      // Validate the form fields
      validateField('site_id').then(_ => {
        void setFieldTouched('site_id', false);
      });

      validateField('location_id').then(_ => {
        void setFieldTouched('location_id', false);
      });
    })

    // Set offsite state
    setOffsite(enabled)
  }

  function toggleAllDay(isAllDay: boolean, {setFieldValue}: FormikProps<EventFormData>) {
    void setFieldValue('is_all_day', isAllDay);
  }

  function toggleRepeats(enabled: boolean, {setFieldValue}: FormikHelpers<EventFormData>) {
    setRepeats(enabled)
    void setFieldValue('recurrence', enabled ? (new RRule({freq: Frequency.DAILY, until: DateTime.now().plus({days: 1}).toJSDate()})).toString() : '')
  }

  function toggleBookable(enabled: boolean, formik: FormikHelpers<EventFormData>) {
    void formik.setFieldValue('is_bookable', enabled);

    if (!enabled) void formik.setFieldValue('external_event_id', undefined).then(() => {
      formik.validateField('external_event_id');
    });
  }

  function handlePublishToggle(enabled: boolean, {setFieldValue}: FormikHelpers<any>) {
    void setFieldValue('is_published', enabled);
    void setFieldValue('is_featured', false);
    void setFieldValue('priority', null);
  }

  function handleFeatureToggle(enabled: boolean, {setFieldValue}: FormikHelpers<any>) {
    void setFieldValue('is_featured', enabled)
  }

  const validationSchema = Yup.object().shape({
    type_id: Yup.number().notOneOf([0], "This field is required").required("This field is required"),
    name: Yup.string().required("This field is required").max(255, 'Maximum of 255 characters'),
    description: Yup.string().max(50000, 'Maximum character limit exceeded'),
    site_id: (offsite || !isMultiSite) ? Yup.number().optional() : Yup.number().required("This field is required"),
    location_id: Yup.number().notOneOf([0], "This field is required").required("This field is required"),
    starts_at: Yup.date().required('This field is required'),
    ends_at:
      Yup.date().when(['starts_at', 'is_all_day'], ([startsAt, is_all_day]: any, schema: any) => {
        if (!startsAt || is_all_day) return schema.required('This field is required');

        const minDate = DateTime.fromJSDate(startsAt).plus({minutes: 1}).toJSDate();
        return schema.min(minDate, "Must be after 'Start' date").required('This field is required');
      }),
    links: Yup.array().of(
      Yup.object().shape({
        text: Yup.string().required('This field is required').max(100, 'Maximum 100 characters'),
        url: Yup.string()
          .required('This field is required')
          .max(250, 'Maximum 250 characters')
          .test('valid-url', urlTest),
        options: Yup.array().of(Yup.string()),
      })
    ),
    external_event_id:
      Yup.string().when(['is_bookable'], ([isBookable]: any, schema: any) => {
        if (isBookable) return schema.required('This field is required');

        return schema;
      }).nullable(),
    priority: Yup.number()
      .integer('Must be a whole number (no decimals allowed)')
      .min(1, 'Must be at least 1')
      .max(100, "Can't be more than 100")
      .nullable(),
  })

  const initialValues: EventFormData = {
    type_id: props.event?.type.id || 0,
    name: props.event?.name || '',
    description: props.event?.description || '',
    starts_at: props.event?.starts_at.toUTC().toISO() || '',
    ends_at: props.event?.ends_at.toUTC().toISO() || '',
    is_all_day: props.event ? props.event.is_all_day : false,
    recurrence: props.event?.recurrence?.toString() ?? '',
    location_type: props.event?.location.type || '',
    location_id: props.event?.location.id || 0,
    is_sustainable: props.event?.is_sustainable ?? false,
    is_bookable: props.event?.is_bookable ?? false,
    external_event_id: props.event?.external_event_id,
    image_id: props.event?.image?.id || null,
    is_published: props.event?.is_published ?? true,
    links: props.event?.links.map(attachedLink => ({
      id: attachedLink.id,
      text: attachedLink.text,
      url: attachedLink.url,
      options: attachedLink.options,
    })),
    is_featured: props.event?.is_featured || false,
    priority: props.event?.priority || null,
    site_id: props.event?.location.type !== 'place' ? props.event?.location.site?.id : 0,
  }

  const sameTimezone = isSameTimezone(timezone);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, formikHelpers) => props.onSubmit(values, formikHelpers)}
      enableReinitialize={!!props.event}
    >
      {formik => {
        const showTimezones = !formik.values.is_all_day && !!formik.values.location_id && !sameTimezone;

        return (
          <Form className="space-y-8 divide-y divide-gray-200">
            <ErrorNotification formik={formik} />

            <div className="shadow sm:rounded-md">
              <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-md">
                <div className="space-y-8 divide-y divide-gray-200">
                  <div>
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Key Information
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Basic details about the {termFor('event')}
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-8">
                      <div className="sm:col-span-3 lg:col-span-2">
                        <Selector
                          label={`${termFor('Event Type')} *`}
                          isLoading={typesLoading}
                          items={eventTypes?.data.map(eventTypeAsItem) || []}
                          selectedId={initialValues.type_id}
                          onChange={item => formik.setFieldValue('type_id', item.id)}
                        />

                        {(!!formik.errors.type_id && formik.touched.type_id) &&
                          <FieldHelp isError>
                            {formik.errors.type_id}
                          </FieldHelp>
                        }
                      </div>
                      <div className="sm:col-span-5">
                        <Field
                          type="text"
                          name="name"
                          id="name"
                          label='Name *'
                          hint='255 chars max'
                          autoComplete="off"
                          maxLength="255"
                          autoFocus
                        />
                      </div>

                      <div className="sm:col-span-8">
                        <Toggle
                          label='Offsite'
                          checked={offsite}
                          onChange={checked => toggleOffsite(checked, formik)}
                        />
                      </div>

                      {(!offsite && isMultiSite) &&
                        <div className="sm:col-span-3 mt-1">
                          <Selector
                            label={`${termFor('Site')} *`}
                            isLoading={sitesLoading}
                            items={sites?.data?.map(siteAsItem) || []}
                            selectedId={formik.values.site_id}
                            onChange={item => selectSite(item, formik)}
                            key='onsite'
                          />

                          {(formik.errors.site_id && formik.touched.site_id) &&
                            <FieldHelp isError>
                              {formik.errors.site_id}
                            </FieldHelp>
                          }
                        </div>
                      }

                      <div className="flex flex-col gap-2 col-span-1 sm:col-span-6 md:col-span-8 lg:col-span-6 xl:col-span-4 xl:col-start-1 mt-1">
                        <Label>Location</Label>
                        <EntitySelector
                          onChange={(items) => selectLocation(items, formik)}
                          siteId={formik.values.site_id}
                          maxSelectedItems={1}
                          buttonText={'Choose a location'}
                          modalTitle='Choose the event location'
                          initialSelectedItems={createSelectedLocationItem(props.event?.location ?? null)}
                          entityTypes={offsite ? [ENTITY_TYPES.PLACE] : [ENTITY_TYPES.EXHIBIT, ENTITY_TYPES.FACILITY, ENTITY_TYPES.ZONE]}
                          disabled={(isMultiSite && !offsite) ? !formik.values.site_id : false}
                        />

                        {(formik.errors.location_id && formik.touched.location_id) &&
                          <FieldHelp isError>
                            {formik.errors.location_id}
                          </FieldHelp>
                        }
                      </div>

                      <div className="sm:col-span-3 sm:col-start-1 xl:col-span-2 xl:col-start-1">
                        <Label htmlFor="starts_at">Start *</Label>
                        <div className="mt-1">
                            <DateField
                              type={formik.values.is_all_day ? 'date' : 'datetime-local'}
                              zone={timezone}
                              name="starts_at"
                              id="starts_at"
                              autoComplete="off"
                              disabled={!formik.values.location_id}
                            />
                        </div>
                      </div>

                      <div className="sm:col-span-3 xl:col-span-2">
                        <Label htmlFor="ends_at">End *</Label>
                        <div className="mt-1">
                            <DateField
                              type={formik.values.is_all_day ? 'date' : 'datetime-local'}
                              zone={timezone}
                              name="ends_at"
                              id="ends_at"
                              autoComplete="off"
                              disabled={!formik.values.location_id}
                            />
                        </div>
                      </div>

                      <div className="sm:col-span-2 sm:mt-8">
                        <Toggle
                          label="Available Today"
                          checked={formik.values.is_all_day}
                          onChange={checked => toggleAllDay(checked, formik)}
                        />
                      </div>

                      {showTimezones && (
                        <div className="sm:col-span-full flex mt-3">
                          <details className="p-3 border border-gray-400 bg-gray-50 rounded-md">
                            <summary className='text-sm text-gray-800 font-medium cursor-pointer'>In a different timezone to the event?</summary>
                            <p className='text-sm text-gray-600 mt-4'>Event timezone ({timezone})</p>
                            <p className='text-sm text-gray-800 mt-2'>{`${formik.values.starts_at ? formattedDateTime(formik.values.starts_at, true, timezone, settings?.localisation.locale) : 'No start date'} - ${formik.values.ends_at ? formattedDateTime(formik.values.ends_at, true, timezone, settings?.localisation.locale) : 'No end date'}`}</p>
                            <p className='text-sm text-gray-600 mt-2'>Your timezone ({systemZone()})</p>
                            <p className='text-sm text-gray-800 mt-2'>{`${formik.values.starts_at ? formattedDateTime(formik.values.starts_at, true, undefined, settings?.localisation.locale) : 'No start date'} - ${formik.values.ends_at ? formattedDateTime(formik.values.ends_at, true, undefined, settings?.localisation.locale) : 'No end date'}`}</p>
                          </details>
                        </div>
                      )}

                      <div className="sm:col-span-8 sm:mt-4">
                        {/*{isEventSeriesInstance &&*/}
                        {/*  <div className='pb-3'>*/}
                        {/*    <Alert message={`You can’t amend the repeating rules of an ${termFor('event')} series.`} />*/}
                        {/*  </div>*/}
                        {/*}*/}

                        <Toggle
                          label="Repeats"
                          description={(isUpdating && isEventSeriesInstance) ? 'Cannot be changed after event series is created' : ''}
                          checked={repeats}
                          onChange={checked => toggleRepeats(checked, formik)}
                          disabled={(isUpdating && isEventSeriesOriginal) || isEventSeriesInstance}
                        />

                        {repeats &&
                          <div className="mt-3">
                            <RepetitionBuilder
                              timezone={timezone}
                              startDate={formik.values.starts_at ? new Date(formik.values.starts_at.toString()) : new Date()}
                              rule={formik.values.recurrence ? RRule.fromString(formik.values.recurrence) : null}
                              onChange={rule => {
                                const ruleString =  rule ? fixRRuleUntil(rule) : '';
                                void formik.setFieldValue('recurrence', ruleString ?? null);
                              }}
                              disabled={(isUpdating && isEventSeriesOriginal) || isEventSeriesInstance}
                              enableForever={!!props.event}
                              untilMax={DateTime.now().plus({years: 2}).toISODate()}
                            />
                          </div>
                        }
                      </div>

                      {/* Sustainability */}
                      <div className="sm:col-span-6">
                        <Toggle
                          label="Sustainable"
                          description={`Whether this ${termFor('event')} is environmentally and socially sustainable`}
                          checked={formik.values.is_sustainable}
                          onChange={checked => formik.setFieldValue('is_sustainable', checked)}
                        />
                      </div>

                      {/* Booking */}
                      {isBookableOperator &&
                        <>
                          <div className="sm:col-span-6">
                            <Toggle
                              label="Bookable"
                              description={`Whether this ${termFor('event')} can be booked`}
                              checked={formik.values.is_bookable}
                              onChange={checked => toggleBookable(checked, formik)}
                            />
                          </div>

                          {formik.values.is_bookable && (
                            <div className="sm:col-span-5">
                              <Field
                                type="text"
                                name="external_event_id"
                                id="external_event_id"
                                label='External Event ID *'
                                hint='255 chars max'
                                autoComplete="off"
                                maxLength="255"
                                autoFocus
                              />
                            </div>
                          )}
                        </>
                      }
                    </div>
                  </div>

                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Additional Information
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Extra information and context about the {termFor('event')}
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-8">

                      <div className="sm:col-span-8">
                        <div className="mt-1">
                          <TextAreaField
                            rows={5}
                            name="description"
                            id="description"
                            label="Description"
                            autoComplete="off"
                            maxChars={50000}
                            hint="Optional. 50,000 chars max."
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-8">
                        <span className="block text-sm font-medium text-gray-700">Image</span>
                        <Uploader
                          icon={PhotographIcon}
                          type={Uploader.types.IMAGE}
                          files={props.event?.image}
                          maxFiles={1}
                          onChange={files => formik.setFieldValue('image_id', files[0] ? files[0].id : null)}
                          onUploadStart={() => setMediaUploading(true)}
                          onUploadEnd={() => setMediaUploading(false)}
                        />
                      </div>

                      <div className="sm:col-span-8">
                        <ToggleSwitch
                          label="Link"
                          checked={!!formik.values.links?.length}
                          onChange={enabled => formik.setFieldValue('links', enabled? [{
                            text: '',
                            url: ''
                          }] : [])}
                        />

                        {!!formik.values.links?.length &&
                          <div className="mt-2">
                            <LinkEditor
                              name="links.0"
                              options={linkOptions}
                            />
                          </div>
                        }
                      </div>
                    </div>
                  </div>

                  <div className="pt-8">
                    <div>
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Publishing
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Make this {termFor('event')} available and/or highlighted in-app
                    </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-4 mt-1">
                        <Toggle
                          label="Published"
                          checked={formik.values.is_published}
                          onChange={checked => handlePublishToggle(checked, formik)}
                          description={`Mark this ${termFor('event')} as published, to display it to your audience`}
                          multiline
                        />
                      </div>
                      {formik.values.is_published &&
                        <>
                          <div className="sm:col-span-6">
                            <Toggle
                              label="Featured"
                              description={`Mark this as a featured ${termFor('event')}, to display it prominently to your audience`}
                              multiline
                              checked={formik.values.is_featured}
                              onChange={checked => handleFeatureToggle(checked, formik)}
                            />
                          </div>
                          <div className="sm:col-span-3">
                            <Field
                              type="number"
                              name="priority"
                              id="priority"
                              label='Priority'
                              hint='Between 1 and 100 (1 is highest priority)'
                              autoComplete="off"
                            />
                          </div>
                        </>
                      }
                    </div>
                  </div>
                </div>
              </div>

              <FormControls formik={formik} preventSubmit={mediaUploading} />
            </div>

            <ShortcutModal show={addingPlace} close={setAddingPlace}>
              <PlaceForm onCancel={() => setAddingPlace(false)} onSubmit={addPlace}/>
            </ShortcutModal>
          </Form>
        )
      }}
    </Formik>
  );
}

export default EventForm;
